import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, ElementRef, HostListener, inject, input, model, output, signal, viewChild, viewChildren } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ClickOutsideDirective } from '@ss/shared/click-outside';
import { firebaseConfig } from '@ss/typings/config';
import { NtaPatient } from '@ss/typings/nta-hunter';
import { mapArray } from 'ngxtension/map-array';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs';

@Component({
	selector: 'nta-patient-search',
	imports: [
		CommonModule,
		ClickOutsideDirective,
		OverlayModule,
		MatIconModule,
		FormsModule,
		MatProgressSpinnerModule
	],
	templateUrl: './patient-search.component.html',
	styleUrl: './patient-search.component.scss',
})
export class PatientSearchComponent {

	loading = signal(false);

	searchInput = viewChild('searchInput', { read: ElementRef });

	searchResultCards = viewChildren('searchResult', { read: ElementRef });

	searchText = model('');

	emptySearch = signal(false);

	searchResultsOpen = signal(false);

	searchResults = signal<NtaPatient[]>([]);

	searchSelectedIndex = signal(0);

	isMac = /Mac/.test(navigator.userAgent);

	http = inject(HttpClient);

	searchPlaceholder = input<string>('Search for Patients');

	selectedFacility = input<string | string[]>();

	noExactMatches = computed(() => this.searchResults().length > 0 && this.searchResults().every((result) => result.fullName.toLowerCase() !== this.searchText().toLowerCase()));

	noSearchResultsSelected = computed(() => this.searchSelectedIndex() === this.searchResults().length);

	allowImport = input<boolean>(false);

	allowCreate = input<boolean>(true);

	showHotkey = input<boolean>(true);

	patientSelected = output<string>();

	patientCreationRequested = output<{ searchText: string; addMode: 'existing' | 'new' }>();

	constructor() {
		toObservable(this.searchText).pipe(
			tap((searchText) => {
				this.emptySearch.set(false);
				if (!searchText) {
					this.searchResults.set([]);
				} else {
					this.searchResultsOpen.set(true);
				}
			}),
			// eslint-disable-next-line @typescript-eslint/no-magic-numbers
			debounceTime(300),
			distinctUntilChanged(),
			tap(() => this.loading.set(true)),
			filter((searchText) => !!searchText),
			switchMap((searchText) => {
				const selectedFacility = this.selectedFacility() || [];
				const facilityIds = Array.isArray(selectedFacility) ? selectedFacility : [selectedFacility];
				return this.http.get<NtaPatient[]>(`${firebaseConfig.functionsUrl}/ntaApi/search`, { params: { query: searchText, facilityIds: facilityIds.join(',') } });
			}),
			mapArray((r) => new NtaPatient(r))
		).subscribe((results) => {
			this.loading.set(false);
			this.searchResults.set(results);
			this.searchResultsOpen.set(true);
			this.searchSelectedIndex.set(0);

			if (results.length === 0) {
				this.emptySearch.set(true);
			}
		});
	}

	@HostListener('document:keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		if (event.metaKey && event.key === 'k' && this.isMac ||
			event.ctrlKey && event.key === 'k' && !this.isMac) {
			this.searchInput().nativeElement.focus();
			event.preventDefault();
			this.searchInput().nativeElement.select();
			this.searchResultsOpen.set(true);
		}

		if (!this.searchResultsOpen()) return;

		switch (event.key) {
			case 'ArrowDown':
				this.searchSelectedIndex.update((x) => Math.min(x + 1, this.noExactMatches() ? this.searchResults().length : this.searchResults().length - 1));
				this.searchResultCards().at(this.searchSelectedIndex()).nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
				break;
			case 'ArrowUp':
				this.searchSelectedIndex.update((x) => Math.max(x - 1, 0));
				this.searchResultCards().at(this.searchSelectedIndex()).nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
				break;
			case 'Enter':
				if (this.noSearchResultsSelected()) {
					this.patientCreationRequested.emit({ searchText: this.searchText(), addMode: 'new' });
					this.clearSearch();
				} else {
					this.searchItemSelect(this.searchResults()[this.searchSelectedIndex()].patientId);
				}
				break;
		}
	}

	searchItemSelect(patientId: string) {
		this.patientSelected.emit(patientId);
		this.clearSearch();
	}

	clearSearch() {
		this.searchText.set('');
		this.searchResultsOpen.set(false);
		this.searchResults.set([]);
		this.emptySearch.set(false);
		this.searchInput().nativeElement.blur();
	}
}
